/**
 * ImageWithBoundingBox component
 *
 * NOTE: if "clickable" is true, this should only be used for Out of Stock.
 * It may get too complicated to use for other cases.
 */
import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import { Image, Box } from "@mantine/core";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { LETTER_INDEX_MAP } from "./config/letterIndexMap";
import { useImageCrop } from "./hooks/useImageCrop";
import { ImageLoading } from "./ImageLoading";

export type Point = [number, number];

interface ImageWithBoundingBoxProps {
  src: string;
  alt: string;
  points?: { points: Point[] }[];
  cropPoints?: Point[];
  variant?: "proposal" | "empty";
  className?: string;
  style?: React.CSSProperties;
  enableZoom?: boolean;
  holdKeysForZoom?: boolean;
  initialZoom?: number;
  minZoom?: number;
  extraBoxContent?: React.ReactNode;
  activeBox?: number | null;
  clickable?: boolean;
  selectedBoxes?: number[];
  modelPredictions?: number[];
  onBoxClick?: (boxIndex: number) => void;
  matches?: number[];
  matchReferenceIndexes?: number[];
  referenceImageIndex?: number;
  enablePulsing?: boolean;
  crop?: boolean;
}

/**
 * Component that displays an image with a bounding box overlay
 * Supports image cropping based on points and different bounding box variants
 */
export const ImageWithBoundingBox: React.FC<ImageWithBoundingBoxProps> = ({
  src,
  alt,
  points = [],
  variant = "proposal",
  className = "",
  style = {},
  enableZoom = true,
  holdKeysForZoom,
  initialZoom = 1,
  minZoom = 1,
  extraBoxContent,
  clickable = false,
  selectedBoxes = [],
  onBoxClick,
  activeBox,
  modelPredictions,
  matches,
  matchReferenceIndexes,
  referenceImageIndex,
  enablePulsing = false,
  cropPoints,
  crop = false,
}) => {
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExtraBoxVisible, setIsExtraBoxVisible] = useState(true);

  // crop
  // a lot of logic is based on the result here
  const cropResult = useImageCrop(src, points, cropPoints, crop);

  // Calculate bounding box from points
  const boundingBoxes = useMemo(
    () =>
      points.map((p) => {
        if (p.points.length === 0) return null;

        // If we have cropPoints, we need to adjust the bounding box calculations
        if (
          crop &&
          cropResult?.cropDimensions &&
          cropResult?.originalDimensions
        ) {
          // Calculate the crop area in percentage terms
          const cropMinX = Math.min(
            ...(cropResult.cropPoints?.map((point) => point[0]) ?? []),
          );
          const cropMaxX = Math.max(
            ...(cropResult.cropPoints?.map((point) => point[0]) ?? []),
          );
          const cropMinY = Math.min(
            ...(cropResult.cropPoints?.map((point) => point[1]) ?? []),
          );
          const cropMaxY = Math.max(
            ...(cropResult.cropPoints?.map((point) => point[1]) ?? []),
          );

          // Adjust the bounding box points relative to the crop area
          const adjustedPoints = p.points
            .map(([x, y]) => {
              // Convert from original image coordinates to cropped image coordinates
              const adjustedX = (x - cropMinX) / (cropMaxX - cropMinX);
              const adjustedY = (y - cropMinY) / (cropMaxY - cropMinY);
              return [adjustedX, adjustedY];
            })
            .filter(([x, y]) => x >= 0 && x <= 1 && y >= 0 && y <= 1); // Filter out points outside the crop area

          if (adjustedPoints.length === 0) return null;

          const minX = Math.min(
            ...adjustedPoints.map((point) => point[0] * 100),
          );
          const maxX = Math.max(
            ...adjustedPoints.map((point) => point[0] * 100),
          );
          const minY = Math.min(
            ...adjustedPoints.map((point) => point[1] * 100),
          );
          const maxY = Math.max(
            ...adjustedPoints.map((point) => point[1] * 100),
          );

          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            centerX: (minX + maxX) / 2,
            centerY: (minY + maxY) / 2,
          };
        } else {
          // Original calculation for non-cropped images
          const minX = Math.min(...p.points.map((p) => p[0] * 100));
          const maxX = Math.max(...p.points.map((p) => p[0] * 100));
          const minY = Math.min(...p.points.map((p) => p[1] * 100));
          const maxY = Math.max(...p.points.map((p) => p[1] * 100));

          return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY,
            centerX: (minX + maxX) / 2,
            centerY: (minY + maxY) / 2,
          };
        }
      }),
    [
      points,
      crop,
      cropPoints,
      cropResult?.cropDimensions,
      cropResult?.originalDimensions,
      cropResult?.cropPoints,
    ],
  );
  const boundingBox = boundingBoxes[0];

  const calculateConstrainedPosition = (
    x: number,
    y: number,
    scale: number,
  ) => {
    if (!boxRef.current || !imageDimensions) return { x, y };

    const containerWidth = boxRef.current.offsetWidth;
    const containerHeight = boxRef.current.offsetHeight;

    // Calculate the scaled image dimensions
    const scaledImageWidth = containerWidth * scale;
    const scaledImageHeight = containerHeight * scale;

    // Calculate the maximum allowed movement to keep image in frame
    const maxX = Math.max(0, scaledImageWidth - containerWidth);
    const maxY = Math.max(0, scaledImageHeight - containerHeight);

    // Clamp the coordinates within bounds
    const constrainedX = Math.max(-maxX, Math.min(maxX, x));
    const constrainedY = Math.max(-maxY, Math.min(maxY, y));

    return { x: Math.min(constrainedX, 0), y: Math.min(constrainedY, 0) };
  };

  // here is where we zoom and position the image on load
  useEffect(() => {
    const scale = initialZoom;
    if (
      boundingBox &&
      transformRef.current &&
      boxRef.current &&
      imageDimensions
    ) {
      // Calculate pixel position
      const xDistanceFromTopLeft =
        (boundingBox.centerX / 100) * boxRef.current.offsetWidth;
      const yDistanceFromTopLeft =
        (boundingBox.centerY / 100) * boxRef.current.offsetHeight;

      const rawX = -(
        xDistanceFromTopLeft * scale -
        boxRef.current.offsetWidth / 2
      );
      const rawY = -(
        yDistanceFromTopLeft * scale -
        boxRef.current.offsetHeight / 2
      );

      // Apply boundary constraints
      const { x, y } = calculateConstrainedPosition(rawX, rawY, scale);

      transformRef.current.setTransform(x, y, scale, 0);

      // Mark as positioned after transform is applied
      // Use a small delay to ensure the transform has been applied
      requestAnimationFrame(() => {
        setIsLoading(false);
      });
    } else if (!crop && (imageDimensions || !points.length)) {
      // If no crop and no bounding box, still wait for image dimensions before showing
      setIsLoading(false);
    }
  }, [boundingBox, imageDimensions]); // Only depend on actual state values

  useEffect(() => {
    if (cropResult?.originalDimensions) {
      setImageDimensions(cropResult.originalDimensions);
    }
  }, [cropResult?.originalDimensions]);

  const getMatchContent = (boxIndex: number) => {
    if (matches) {
      for (let i = 0; i < matches.length; i++) {
        if (
          matches[i] === boxIndex &&
          matchReferenceIndexes?.[i] === referenceImageIndex
        ) {
          const letter =
            LETTER_INDEX_MAP[i.toString() as keyof typeof LETTER_INDEX_MAP];
          return (
            <div
              className="flex items-center justify-center w-full h-full"
              style={{ color: "#fff", fontWeight: 500 }}
            >
              {letter}
            </div>
          );
        }
      }
    }
    return null;
  };

  /**
   * This function shows/hides "extraBoxContent" based on the active box and the transformation state.
   * It is used to show/hide the product preview tooltip.
   */
  const checkExtraBoxVisibility = useCallback(() => {
    // Guard clauses: exit if refs aren't ready or no box is active
    if (
      activeBox === null ||
      activeBox === undefined ||
      !transformRef.current ||
      !boxRef.current
    ) {
      setIsExtraBoxVisible(true); // Reset to visible when no box is active
      return;
    }
    const activeBoundingBox = boundingBoxes[activeBox];
    if (!activeBoundingBox) return;

    // Get current transformation state and container dimensions
    const { scale, positionX, positionY } =
      transformRef.current.instance.transformState;
    const container = boxRef.current;
    const containerWidth = container.offsetWidth;
    const containerHeight = container.offsetHeight;

    // Calculate the viewport boundaries in the content's coordinate system (in pixels)
    const viewLeft = -positionX / scale;
    const viewTop = -positionY / scale;
    const viewRight = viewLeft + containerWidth / scale;
    const viewBottom = viewTop + containerHeight / scale;

    // Calculate the active box's boundaries in the content's coordinate system (in pixels)
    const boxLeft = (activeBoundingBox.x / 100) * containerWidth;
    const boxTop = (activeBoundingBox.y / 100) * containerHeight;
    const boxRight = boxLeft + (activeBoundingBox.width / 100) * containerWidth;
    const boxBottom =
      boxTop + (activeBoundingBox.height / 100) * containerHeight;

    // Check if the two rectangles overlap
    const isVisible =
      boxLeft < viewRight &&
      boxRight > viewLeft &&
      boxTop < viewBottom &&
      boxBottom > viewTop;

    setIsExtraBoxVisible(isVisible);
  }, [activeBox, boundingBoxes]);

  // Calculate bounding box styles
  const getBoundingBoxStyle = (box: any, boxIndex: number) => {
    if (!box || !imageDimensions || !boxRef.current) return {};

    // const pxWidth = (boundingBox.width / 100) * (boxRef.current?.offsetWidth ?? 0 / imageDimensions?.width);
    // const pxHeight = (boundingBox.height / 100) * (boxRef.current?.offsetHeight ?? 0 / imageDimensions?.height);
    // const originalBoxWidth = boundingBox.width * imageDimensions.width;
    // const originalBoxHeight = boundingBox.height * imageDimensions.height;
    // const pxHeight = pxWidth * originalBoxHeight / originalBoxWidth;

    const isSelected = selectedBoxes.includes(boxIndex);
    // const isPrediction =
    const baseStyle: React.CSSProperties = {
      position: "absolute",
      top: `${box.y}%`,
      left: `${box.x}%`,
      // width: `${pxWidth}px`,
      // height: `${pxHeight}px`,
      width: `${box.width}%`,
      height: `${box.height}%`,
      pointerEvents: "auto",
      zIndex: 10,
    };

    // Add pulsing animation if enabled
    if (enablePulsing) {
      baseStyle.animation = "pulse 0.5s ease-in-out infinite";
    }

    if (variant === "proposal" && modelPredictions?.includes(boxIndex)) {
      return {
        ...baseStyle,
        border: "1.5px solid #3b82f6",
        borderRadius: "5px",
        backgroundColor: isSelected ? "rgba(59, 130, 246, 0.4)" : "transparent",
      };
    } else if (variant === "proposal") {
      return {
        ...baseStyle,
        border: "1px solid #FF00FF",
        backgroundColor: isSelected ? "rgba(255, 0, 255, 0.4)" : "transparent",
      };
    } else {
      return {
        ...baseStyle,
        border: "1.5px solid #ef4444",
        backgroundColor: isSelected ? "rgba(239, 68, 68, 0.4)" : "transparent",
      };
    }
  };

  const imageContent = (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* CSS for pulsing animation */}
      {enablePulsing && (
        <style>
          {`
            @keyframes pulse {
              0% {
                transform: scale(1);
              }
              50% {
                transform: scale(1.2);
              }
              100% {
                transform: scale(1);
              }
            }
          `}
        </style>
      )}
      <div>
        <Image
          src={crop ? cropResult?.croppedImageUrl : src}
          alt={alt}
          radius="md"
          className="border border-gray-200 shadow-sm"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={() => {
            setIsLoading(false);
          }}
        />
      </div>

      {boundingBoxes?.length > 0 &&
        boundingBoxes.map((box, i) => (
          <div
            onClick={() => {
              if (!clickable) return;
              onBoxClick?.(i);
            }}
            key={i}
            style={getBoundingBoxStyle(box, i)}
          >
            {getMatchContent(i)}
            {activeBox === i && isExtraBoxVisible && extraBoxContent}
          </div>
        ))}
    </div>
  );

  if (!enableZoom) {
    return (
      <Box
        className={`relative ${className}`}
        ref={boxRef}
        style={{
          overflow: "hidden",
          width: "100%",
          height: "auto",
          // aspectRatio: "3/4",
          ...style,
        }}
      >
        {imageContent}
      </Box>
    );
  }

  const wheelConfig = holdKeysForZoom
    ? {
        step: 0.2,
        activationKeys: ["Control", "Meta"],
      }
    : undefined;

  return (
    <Box
      className={`relative ${className}`}
      ref={boxRef}
      style={{
        width: "100%",
        // height: '300px',
        // overflow: 'hidden',
        aspectRatio: crop ? "1/1" : "auto",
        ...style,
      }}
    >
      {/* Loading overlay */}
      {isLoading && <ImageLoading />}

      <TransformWrapper
        ref={transformRef}
        initialScale={1}
        minScale={minZoom}
        maxScale={Infinity}
        wheel={wheelConfig}
        pinch={{ step: 5 }}
        doubleClick={{ disabled: true }}
        onTransformed={checkExtraBoxVisibility}
        panning={{ velocityDisabled: true }}
      >
        <TransformComponent
          wrapperStyle={{ width: "100%", height: "100%" }}
          contentStyle={{ width: "100%", height: "100%" }}
        >
          {imageContent}
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
};
